import type { ReactNode } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';
// EAA-1846: `@ant-design/cssinjs` is a DIRECT dependency in package.json, and must stay
// one. It is also antd's own transitive dep, so in the monorepo it resolves through
// hoisting and this import works locally whether or not it is declared — which is exactly
// how it shipped undeclared. The Docker build installs this app as a standalone workspace
// (its package.json plus the vendored packages), where a transitive dep of antd is not
// resolvable as a direct import, and Rollup failed with "failed to resolve import
// @ant-design/cssinjs". Keep the range in step with antd's (`^2.1.2`) so pnpm dedupes to
// ONE copy: a second copy would give StyleProvider a different context than antd's
// internals use, and the provider would silently do nothing.
import { StyleProvider } from '@ant-design/cssinjs';
import viVN from 'antd/locale/vi_VN';
import { getColors } from '@ai-content-hub/shared-styles';
import { UIProvider } from '@ai-content-hub/ui/providers';
import { UIColorsProvider, createAntdTheme } from '@ai-content-hub/ui/theme';
import { useTheme } from '@/theme/useTheme';

/**
 * EAA-1804: mounts everything `@ai-content-hub/ui` needs, once, at the shell.
 *
 * The portal now consumes the shared kit directly instead of keeping native
 * twins of it. That reverses the earlier "standalone repo, no `@ai-content-hub/*`"
 * stance recorded in PLAN-SSO-OIDC-Backend.md §8 — worth knowing the cost:
 * this repo resolves the kit through the monorepo's pnpm workspace, so a
 * standalone `git clone emg-portal-fe && pnpm install` no longer builds. It
 * only builds checked out as `apps/portal` inside `emg-ai-content-hub-fe`.
 * §8's own answer to that was a versioned artifact via the A4.2 pipeline; if
 * this repo ever needs to build on its own again, that is the path back.
 *
 * Three seams the kit reads from, all mounted here:
 *   - antd `ConfigProvider theme` — the component sheet, via `createAntdTheme`.
 *     Outermost so the locale provider inside inherits it.
 *   - `StyleProvider layer` — puts antd's runtime CSS-in-JS into `@layer antd`
 *     (EAA-1846). antd injects its styles into <head> AFTER the app's own
 *     stylesheet has loaded, so while both were unlayered antd won every tie
 *     and the portal's `.ant-*` overrides had to out-specify it, or shout
 *     `!important`. Layered, `global.css`'s `chrome`/`kit`/`page` tiers all
 *     rank above `antd` by tier, whatever the selectors say.
 *   - `UIColorsProvider` — the `useUIColors()` seam. Fed the same palette the
 *     CSS custom properties in `tokens.css` carry, so kit components and the
 *     portal's own stylesheets cannot disagree.
 *   - `UIProvider` — the `useUIText()` catalog, pinned to Vietnamese (NFR-08).
 *
 * Deliberately NOT mounted: `AuthCapabilityProvider`. Its default fails closed,
 * and no portal call site passes `auth` — the portal gates by *absence* (a
 * permission-less nav item is not rendered at all), which is what the design
 * sheet prescribes. A future call site that does pass `auth` will correctly
 * render denied until a real capability source is wired in here.
 */
export function UiKitProvider({ children }: { children: ReactNode }) {
    // EAA-1844: follows the real theme now — <ThemeProvider> mounts above this
    // component (see main.tsx) specifically so this seam could read it instead
    // of the EAA-1778 hardcoded `getColors(false)`.
    const { theme } = useTheme();
    const colors = getColors(theme === 'dark');

    return (
        <StyleProvider layer>
            <ConfigProvider theme={createAntdTheme(colors)}>
                <UIColorsProvider colors={colors}>
                    <UIProvider locale="vi" antdLocale={viVN}>
                        {/* antd's <App> is what the kit's `useToast` reads through
                            `App.useApp()`. It replaces the portal's own
                            <ToastProvider>; without it the hook silently degrades
                            to unthemed static toasts rendered outside this
                            ConfigProvider, which is a failure mode with no visible
                            error — hence mounting it here rather than per screen. */}
                        <AntApp component={false}>{children}</AntApp>
                    </UIProvider>
                </UIColorsProvider>
            </ConfigProvider>
        </StyleProvider>
    );
}
